/**
 * Runtime de UI de plugins (isomórfico — sem dependência de Node).
 *
 * Fonte única usada pelo frontend do Markedesk e pelos scripts de plugin.
 * Cada plugin declara `provides.frontendScript` (string JS); ao carregar, o
 * script é executado recebendo a API global `markedesk`, que registra:
 *  - hooks de decisão (registerUIHook)
 *  - interceptadores de fluxo (intercept: before/on/after)
 *  - modais HTML no contexto do app (openHtmlModal)
 *
 * ATENÇÃO: o script roda no contexto do app (acesso amplo) — modelo de
 * extensão de navegador: a confiança é de quem instala o plugin.
 */

import type { UIHookContextMap, UIEventContextMap, PluginInfo } from "./types";
import { KNOWN_SLOTS, isDeprecatedSlot, isKnownSlot } from "./slotNames";
import { pluginLog } from "./debug";

export type InterceptPhase = "before" | "on" | "after";

/** Handler de hook de decisão. Tipado pelo nome do hook quando conhecido. */
export type UIHookHandler<C = any> = (context: C) => any;

/** Handler de interceptação (middleware async). Tipado pelo contexto do evento. */
export type InterceptHandler<C = any> = (ctx: C, next: () => Promise<void>) => void | Promise<void>;

// Resolve o tipo de contexto a partir do nome (cai em `any` se desconhecido).
type HookCtx<K extends string> = K extends keyof UIHookContextMap ? UIHookContextMap[K] : any;
type EventCtx<K extends string> = K extends keyof UIEventContextMap ? UIEventContextMap[K] : any;

export interface OpenHtmlModalOptions {
  title?: string;
  width?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Modo HTML: injeta esta string no modal (innerHTML). Opcional se usar `jsx`.
   */
  html?: string;
  // el é um HTMLElement no browser; tipado como any para o SDK não exigir lib DOM.
  onMount?: (el: any, resolve: (data?: any) => void, close: () => void) => void;
  /**
   * Modo REACT: corpo de `(React, props) => <JSX/>` (string). O host transpila
   * (Babel) e monta React de verdade — hooks/context funcionam (React do app).
   * O componente recebe `props` (abaixo) mesclado com `{ resolve, close }` para
   * fechar o modal. Tem prioridade sobre `html`.
   */
  jsx?: string;
  /** Props passadas ao componente JSX (além de resolve/close). */
  props?: Record<string, any>;
}

/**
 * Controlador de um slot de "wrap" (PluginWrap). O app envolve um elemento seu
 * e entrega ao plugin a API imperativa `slot` + eventos de ciclo de vida. Tudo
 * via DOM real — funciona no React buildado (sem JSX/import no plugin).
 */
export interface SlotController {
  onMount?: (slot: SlotApi) => void;
  onContextChange?: (slot: SlotApi) => void;
  onUnmount?: (slot: SlotApi) => void;
}

/** Elemento que o plugin injeta no slot via slot.add(). */
export interface SlotElement {
  /** HTML do elemento (sem closures — string auto-contida). */
  html?: string;
  /** Handler de clique no elemento adicionado. */
  onClick?: (slot: SlotApi) => void;
  /** Chamado após injetar; recebe o nó DOM para ligar eventos. */
  onMount?: (el: any, slot: SlotApi) => void;
}

/** API imperativa entregue ao controlador do slot (implementada no app).
 *  `C` tipa o `context` daquele slot (ver SlotContextMap). Default `any` mantém
 *  retrocompatibilidade com slots que não tipam. */
export interface SlotApi<C = any> {
  /** Dados do ponto — tipados por slot via SlotContextMap (ex: connection-status
   *  tem `channel`, `status`, `connection`...). Antes era `any`. */
  context: C;
  /** routePath do plugin (/p/{hash}/{pluginId}) para montar chamadas. */
  routePath?: string | null;
  /** Cliente HTTP autenticado do app (axios). */
  api: any;
  hide(): void;
  show(): void;
  remove(): void;
  /** Injeta um elemento no slot; retorna handle para removê-lo. */
  add(el: SlotElement): { remove(): void };
  /** Remove o que o plugin adicionou e reexibe o original (estado neutro). */
  clear(): void;
  /** Abre um modal (HTML ou JSX) no contexto do app. */
  openModal(opts: OpenHtmlModalOptions): Promise<any>;
  /**
   * Monta um componente REACT escrito em JSX (string) no slot, usando o React do
   * app (Babel transpila no browser — sem build do plugin). `jsx` é o corpo de
   * `(React, props) => <JSX/>`. Ex.: `slot.mountJsx("return <div>{props.x}</div>", { x })`.
   * Compila 1x por string (React preserva estado/effects entre re-renders).
   */
  mountJsx(jsx: string, props?: Record<string, any>): void;
  /**
   * Assina mensagens novas do ticket (via socket autenticado do app) e chama
   * `cb` quando chega mensagem do ticketId. Retorna função p/ cancelar.
   */
  /** O callback recebe o payload do socket (`{ action, message, ticket, contact }`). */
  onTicketMessage(ticketId: number | string, cb: (payload?: any) => void): () => void;
}

export interface MarkedeskUIApi {
  pluginId: string;
  /** priority: menor roda primeiro (default 100). Empate mantém ordem de registro. */
  registerUIHook<K extends string>(name: K, handler: UIHookHandler<HookCtx<K>>, priority?: number): void;
  intercept<K extends string>(event: K, phase: InterceptPhase, handler: InterceptHandler<EventCtx<K>>, priority?: number): void;
  openHtmlModal(opts: OpenHtmlModalOptions): Promise<any>;
  /** Registra um controlador para um slot de wrap (PluginWrap). */
  registerSlot(name: string, controller: SlotController, priority?: number): void;
}

const DEFAULT_PRIORITY = 100;

interface HookEntry { pluginId: string; handler: UIHookHandler; priority: number; seq: number }
interface InterceptEntry { pluginId: string; handler: InterceptHandler; priority: number; seq: number }

interface SlotEntry { pluginId: string; controller: SlotController; priority: number; seq: number }

const registry = new Map<string, HookEntry[]>();
const interceptors = new Map<string, InterceptEntry[]>();
const slotControllers = new Map<string, SlotEntry[]>();
let seqCounter = 0;

/** Ordena por priority (asc); empate pela ordem de registro (seq). */
function insertSorted<T extends { priority: number; seq: number }>(list: T[], entry: T): void {
  list.push(entry);
  list.sort((a, b) => (a.priority - b.priority) || (a.seq - b.seq));
}
const executedScripts = new Set<string>();
const PHASES: InterceptPhase[] = ["before", "on", "after"];

// O frontend (PluginHtmlModalHost) registra aqui a função que abre o modal.
let modalOpener: ((opts: OpenHtmlModalOptions & { resolve: (v?: any) => void }) => void) | null = null;
export function setModalOpener(fn: typeof modalOpener): void { modalOpener = fn; }

/** Abre um modal HTML no contexto do app (uso direto, fora de um plugin script). */
export function openHtmlModal(opts: OpenHtmlModalOptions): Promise<any> {
  if (!modalOpener) return Promise.resolve(undefined);
  return new Promise((resolve) => modalOpener!({ ...opts, resolve }));
}

function makeApi(pluginId: string): MarkedeskUIApi {
  return {
    pluginId,
    registerUIHook(name, handler, priority = DEFAULT_PRIORITY) {
      if (typeof handler !== "function") return;
      if (!registry.has(name)) registry.set(name, []);
      insertSorted(registry.get(name)!, { pluginId, handler, priority, seq: seqCounter++ });
    },
    intercept(event, phase, handler, priority = DEFAULT_PRIORITY) {
      if (typeof handler !== "function" || PHASES.indexOf(phase) < 0) return;
      const key = event + ":" + phase;
      if (!interceptors.has(key)) interceptors.set(key, []);
      insertSorted(interceptors.get(key)!, { pluginId, handler, priority, seq: seqCounter++ });
    },
    openHtmlModal(opts) {
      if (!modalOpener) return Promise.resolve(undefined);
      return new Promise((resolve) => modalOpener!({ ...opts, resolve }));
    },
    registerSlot(name, controller, priority = DEFAULT_PRIORITY) {
      if (!controller || typeof controller !== "object") return;
      // Nome fora do catálogo é erro do plugin e não depende de nenhuma tela ter
      // aberto — acusa já, no registro.
      if (isDev() && !isKnownSlot(name)) {
        warnOnce(
          "unknown:" + name + ":" + pluginId,
          `[sdk/ui] plugin "${pluginId}" registrou um controlador para o slot "${name}", ` +
            `que não existe no catálogo — nada será chamado. ` +
            `Slots válidos: ${KNOWN_SLOTS.slice().sort().join(", ")}`
        );
      } else if (isDev() && isDeprecatedSlot(name)) {
        warnOnce(
          "deprecated:" + name + ":" + pluginId,
          `[sdk/ui] plugin "${pluginId}" usou o slot "${name}", que nenhum ponto do app ` +
            `renderiza (mantido só por compatibilidade e será removido).`
        );
      }
      if (!slotControllers.has(name)) slotControllers.set(name, []);
      insertSorted(slotControllers.get(name)!, { pluginId, controller, priority, seq: seqCounter++ });
      pluginLog("registerSlot", name, "por", pluginId);
    },
  };
}

/** Controladores registrados para um slot de wrap (ordenados por priority). */
export function getSlotControllers(name: string): Array<{ pluginId: string; controller: SlotController }> {
  return (slotControllers.get(name) || []).map((e) => ({ pluginId: e.pluginId, controller: e.controller }));
}

/** true se há ao menos um controlador para o slot (gate de hot path). */
export function hasSlotController(name: string): boolean {
  return (slotControllers.get(name) || []).length > 0;
}

// ── Diagnóstico: controlador registrado x ponto que o executa ──────────────
//
// Registrar um controlador para um slot que ninguém executa não produz erro
// algum: o Map recebe a entrada e nada acontece. Foi assim que o botão do
// plugin-tour (navbar) e o "Enviar template" do notificamehub (new-ticket)
// ficaram mortos sem ninguém notar.
//
// São dois modos de falha DIFERENTES, e cada um tem seu aviso:
//  1. nome que não existe no catálogo (typo, slot removido) — detectável na
//     hora do registro, sem depender de nenhuma tela ter aberto;
//  2. slot que o app renderiza, mas em nenhum ponto com o motor de wrap ligado
//     (`wrap={false}`, ou slot de menu, que não aceita wrapper) — só dá para
//     saber depois que a tela montou.
//
// Slot conhecido que ainda não apareceu em tela NÃO é avisado: pode ser apenas
// uma página que o usuário não abriu, e acusar isso seria falso positivo.

const mountedSlots = new Set<string>();      // slots que algum ponto renderizou
const wrapEnabledSlots = new Set<string>();  // ...com o motor de wrap ligado
const activeWraps = new Map<string, number>(); // "slot|contexto" -> wraps vivos
const warnedOnce = new Set<string>();

function isDev(): boolean {
  try {
    return typeof process !== "undefined" && !!process.env && process.env.NODE_ENV !== "production";
  } catch (e) {
    return false;
  }
}

function warnOnce(key: string, message: string): void {
  if (warnedOnce.has(key)) return;
  warnedOnce.add(key);
  // eslint-disable-next-line no-console
  console.warn(message);
}

function ownersOf(name: string): string {
  const entries = slotControllers.get(name) || [];
  return Array.from(new Set(entries.map((e) => e.pluginId))).join(", ");
}

/**
 * O app anuncia que renderizou este slot. `wrap` diz se aquele ponto ligou o
 * motor imperativo — é o que separa "a tela não abriu" de "a tela abriu e nunca
 * vai chamar o controlador".
 */
export function markSlotMounted(name: string, opts?: { wrap?: boolean }): void {
  if (!name) return;
  mountedSlots.add(name);
  if (opts && opts.wrap) wrapEnabledSlots.add(name);
}

/** Slots que algum componente do app montou nesta sessão. */
export function getMountedSlots(): string[] {
  return Array.from(mountedSlots);
}

/** Slots que montaram com o motor de wrap ligado. */
export function getWrapEnabledSlots(): string[] {
  return Array.from(wrapEnabledSlots);
}

/**
 * Registra uma montagem viva de wrap e devolve a função de baixa.
 *
 * Dois pontos DIFERENTES da tela servindo o mesmo slot com o mesmo contexto
 * montam o controlador duas vezes — dois `onMount`, dois SlotApi — e um plugin
 * que guarda estado global (o notificamehub e seu unsubscribe de socket) perde
 * a referência da primeira montagem. Contexto diferente (uma linha por conexão,
 * um item por ticket) é montagem legítima e não gera aviso.
 */
export function trackWrapMount(name: string, contextKey: string): () => void {
  if (!name) return () => undefined;
  const key = name + "|" + contextKey;
  const live = (activeWraps.get(key) || 0) + 1;
  activeWraps.set(key, live);
  if (live > 1 && isDev()) {
    warnOnce(
      "dup:" + key,
      `[sdk/ui] slot "${name}" está montado com wrap em ${live} pontos com o MESMO contexto — ` +
        `o controlador de ${ownersOf(name) || "plugin"} roda ${live} vezes. ` +
        `Passe wrap={false} no ponto que só desenha as ações declaradas.`
    );
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const rest = (activeWraps.get(key) || 1) - 1;
    if (rest <= 0) activeWraps.delete(key);
    else activeWraps.set(key, rest);
  };
}

/**
 * Avisa sobre controladores que nunca serão chamados porque nenhum ponto do
 * slot liga o motor de wrap. Só faz sentido depois que a UI montou — chame com
 * um atraso, ou de um efeito no fim do boot.
 */
export function warnOrphanSlotControllers(): void {
  if (!isDev()) return;
  slotControllers.forEach((entries, name) => {
    if (!entries.length) return;
    if (wrapEnabledSlots.has(name)) return; // alguém executa: tudo certo
    if (!mountedSlots.has(name)) return;    // tela ainda não aberta: sem evidência
    warnOnce(
      "nowrap:" + name,
      `[sdk/ui] slot "${name}" é renderizado, mas nenhum ponto liga o motor de wrap ` +
        `(wrap={false}, ou slot de menu, que não aceita wrapper) — o controlador de ` +
        `${ownersOf(name)} nunca será chamado. Nesse slot, use uma ação declarada ` +
        `(provides.actions) em vez de registerSlot.`
    );
  });
}

/**
 * Plugins rodados via esbuild/tsx (ex: tsx watch, tsup) têm suas funções
 * transformadas em memória ANTES do .toString() capturar o código-fonte —
 * o esbuild injeta `__name(fn, "nome")` para preservar o nome em funções
 * nomeadas dentro de outras funções. serializeFn captura esse código já
 * transformado (não o original), então o script gerado carrega chamadas a
 * `__name` sem a declaração do helper junto → ReferenceError no browser,
 * quebrando o slot inteiro antes de rodar qualquer lógica. Polyfill no-op
 * (mesmo contrato do helper real: retorna a própria função).
 */
function __name<T>(fn: T, _name?: string): T {
  return fn;
}

/** Executa o frontendScript de uma lista de plugins (idempotente por id). */
export function runPluginScripts(plugins: PluginInfo[]): void {
  for (const plugin of plugins || []) {
    const script = plugin?.provides?.frontendScript;
    const id = plugin?.id || plugin?.channel;
    if (!script || !id || executedScripts.has(id)) continue;
    executedScripts.add(id);
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function("markedesk", "__name", script);
      fn(makeApi(id), __name);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[sdk/ui] erro no frontendScript do plugin ${id}:`, err);
    }
  }
}

/** Roda todos os handlers de um hook e retorna os resultados. */
export function runHook<K extends string>(name: K, context?: HookCtx<K>): Array<{ pluginId: string; value: any }> {
  const entries = registry.get(name) || [];
  const results: Array<{ pluginId: string; value: any }> = [];
  for (const { pluginId, handler } of entries) {
    try {
      results.push({ pluginId, value: handler(context) });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[sdk/ui] erro no hook "${name}" (${pluginId}):`, err);
    }
  }
  return results;
}

/** true se algum handler do hook retornar truthy. */
export function anyHookTrue<K extends string>(name: K, context?: HookCtx<K>): boolean {
  return runHook(name, context).some((r) => !!r.value);
}

async function runPhase(event: string, phase: InterceptPhase, ctx: any): Promise<boolean> {
  const chain = interceptors.get(event + ":" + phase) || [];
  let cancelled = false;
  let idx = -1;
  async function dispatch(i: number): Promise<void> {
    if (i <= idx) return;
    idx = i;
    const entry = chain[i];
    if (!entry) return;
    let calledNext = false;
    try {
      await entry.handler(ctx, async () => { calledNext = true; await dispatch(i + 1); });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[sdk/ui] erro no interceptor ${event}:${phase} (${entry.pluginId}):`, err);
      calledNext = true;
    }
    if (!calledNext && phase === "before") cancelled = true;
  }
  await dispatch(0);
  return !cancelled;
}

/** Executa uma ação pelo pipeline before -> on(coreAction) -> after. */
export async function runPipeline<K extends string>(
  event: K,
  ctx: EventCtx<K>,
  coreAction: (ctx: EventCtx<K>) => Promise<any>
): Promise<any> {
  const proceed = await runPhase(event, "before", ctx);
  if (!proceed) return undefined;
  await runPhase(event, "on", ctx);
  ctx.result = await coreAction(ctx);
  await runPhase(event, "after", ctx);
  return ctx.result;
}
